import { DateTime } from 'luxon';
import { FOLLOW_UP_TOTAL_WEEKS } from '../../constants';
import { isoToTimezoneDateTime, parseDateOnlyInTimezone } from '../../utils';
import { weekMonday } from './datetime.util';

export const buildFollowUpWindows = (
  anchorISO: string,
  timezone?: string,
  totalWeeks: number = FOLLOW_UP_TOTAL_WEEKS,
  admitISO: string = anchorISO,
  spanStartOverrideISO?: string,
): FollowUpWindow[] => {
  // Use the anchor end time (intake slot end) as the query start so
  // availability that overlaps the intake is excluded.
  const spanStart = DateTime.fromISO(spanStartOverrideISO ?? anchorISO, { zone: 'utc' });

  // Cap at totalWeeks weeks from admit date (not intake) so follow-ups stay
  // within the same window shown in the appointments panel. admitISO is
  // date-only (e.g. clinicalProfile.admitDate) when explicitly provided, but
  // defaults to the full-timestamp anchorISO when omitted — only the latter
  // is a real instant that needs converting into the patient's timezone.
  const isAdmitISODateOnly = !admitISO.includes('T');
  const baseMonday = weekMonday(
    isAdmitISODateOnly
      ? parseDateOnlyInTimezone(admitISO, timezone)
      : isoToTimezoneDateTime(admitISO, timezone),
  );
  const spanEnd = baseMonday.plus({ weeks: totalWeeks }).toUTC();

  return [
    {
      startISO: spanStart.toISO()!,
      endISO: spanEnd.toISO()!,
    },
  ];
};

/**
 * Returns true when the follow-up availability window is invalid because the
 * admit date is too far in the past (end <= start).
 */
export const isFollowUpWindowExpired = (
  anchorEndISO: string,
  timezone?: string,
  totalWeeks?: number,
  admitISO?: string,
): boolean => {
  if (!admitISO) return false;
  const [window] = buildFollowUpWindows(anchorEndISO, timezone, totalWeeks, admitISO, anchorEndISO);
  return window.endISO <= window.startISO;
};
